import * as React from 'react';

import { IResponsiveImage } from '@utils/images';

import coBrandedImage from '@images_responsive/partnerships/co-branded.png';
// Import the images for each step
import locationImage from '@images_responsive/partnerships/location.png';
import logisticImage from '@images_responsive/partnerships/logistic.png';

import { useInViewOnce } from '../../landing_pages/smart_storage_v2/shared/use_in_view_once';
import {
  Container,
  DesktopProgressBar,
  HowItWorksSection,
  LaptopContainer,
  LaptopImage,
  LeftSection,
  LeftSectionMobile,
  MainHeading,
  MobileProgressBar,
  RightSection,
  StepContent,
  StepDescription,
  StepTitle,
} from './how_it_works.styles';
import {
  SegmentProgressBar,
  useAutoSegmentProgress,
} from './shared/segment_progress_bar';

interface Step {
  title: string;
  description: React.ReactElement;
  image: IResponsiveImage;
  alt: string;
}

const steps: Step[] = [
  {
    title: 'Select your locations',
    description: (
      <>
        Identify the properties, campuses, or offices you want to activate.
        We’ll help you determine the right buildings and rollout plan based on
        resident density, seasonality, and expected demand.
      </>
    ),
    image: locationImage,
    alt: 'Location selection illustration',
  },
  {
    title: 'Align on logistics',
    description: (
      <>
        Confirm routes, frequency, and service style: dedicated weekly service
        or on-demand app pickups. We’ll help you choose the right fit.
      </>
    ),
    image: logisticImage,
    alt: 'Logistics alignment illustration',
  },
  {
    title: 'Launch your co-branded experience',
    description: (
      <>
        Launch with a custom landing page or resident-portal integration — we’ll
        provide all creative and onboarding materials.
      </>
    ),
    image: coBrandedImage,
    alt: 'Co-branded experience illustration',
  },
];

export const HowItWorksComponent: React.FC = () => {
  const [sectionRef, inView] = useInViewOnce<HTMLDivElement>(0.3);
  const [state, setActiveIndex] = useAutoSegmentProgress(steps.length, {
    enabled: inView,
    resetOnEnable: true,
  });

  return (
    <HowItWorksSection ref={sectionRef}>
      <Container>
        <LeftSection>
          <LaptopContainer>
            <LaptopImage
              image={steps[state.active].image}
              alt={steps[state.active].alt}
              sizes="(min-width: 768px) 40vw, 80vw"
            />
          </LaptopContainer>
        </LeftSection>

        <RightSection>
          <MainHeading>How it works</MainHeading>

          <LeftSectionMobile>
            <LaptopContainer>
              <LaptopImage
                image={steps[state.active].image}
                alt={steps[state.active].alt}
                sizes="(min-width: 768px) 40vw, 80vw"
              />
            </LaptopContainer>
            <MobileProgressBar>
              <SegmentProgressBar
                total={steps.length}
                activeIndex={state.active}
                progressPct={state.progress}
                onSelect={(i) => setActiveIndex(i)}
                variant="partnerships"
              />
            </MobileProgressBar>
          </LeftSectionMobile>

          <DesktopProgressBar>
            <SegmentProgressBar
              total={steps.length}
              activeIndex={state.active}
              progressPct={state.progress}
              onSelect={(i) => setActiveIndex(i)}
              variant="partnerships"
            />
          </DesktopProgressBar>

          <StepContent>
            <StepTitle>{steps[state.active].title}</StepTitle>
            <StepDescription>{steps[state.active].description}</StepDescription>
          </StepContent>
        </RightSection>
      </Container>
    </HowItWorksSection>
  );
};
